(function () {
  if (window.vaahiniInitialized) return;
  window.vaahiniInitialized = true;

  const APP_URL = (
    document.querySelector('meta[name="vaahini-app-url"]')?.content ||
    'https://premium-transaction-app.vercel.app'
  ).replace(/\/$/, '');

  const SHIPROCKET_STORAGE_KEY = 'shiprocketCheckoutOrderDetails';
  const TOTAL_TOLERANCE_PAISE = 100;

  let clickPassThrough = false;
  let submitPassThrough = false;
  let applying = false;
  let syncingDiscount = false;
  let cartMutationPromises = [];

  window.vaahiniPreGeneratedCode = null;
  window.vaahiniPreGeneratedInvoiceUrl = null;
  window.vaahiniCheckoutButtonLoading = false;
  window.vaahiniCartVerified = false;

  const nativeFetch = window.fetch.bind(window);

  // ─── Bundle pricing (single source of truth) ───────────────────────────────

  function expandUnitPrices(cart) {
    const prices = [];
    (cart?.items || []).forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        prices.push(item.original_price);
      }
    });
    return prices;
  }

  function calculateExpectedTotal(cart) {
    const allPrices = expandUnitPrices(cart);
    if (!allPrices.length) return 0;

    const numGroupsOf4 = Math.floor(allPrices.length / 4);
    const remainder = allPrices.length % 4;
    const sortedPrices = [...allPrices].sort((a, b) => a - b);

    let totalPaid = numGroupsOf4 * 159900;
    if (remainder === 3) totalPaid += 129900;
    else if (remainder === 2) totalPaid += sortedPrices[1] || 0;
    else if (remainder === 1) totalPaid += sortedPrices[0] || 0;
    return totalPaid;
  }

  function getBundleSavings(cart) {
    if (!cart?.items?.length) return 0;
    const originalSubtotal = cart.items.reduce(
      (sum, item) => sum + item.original_price * item.quantity,
      0
    );
    return Math.max(0, originalSubtotal - calculateExpectedTotal(cart));
  }

  function cartTotalIsCorrect(cart) {
    if (!cart) return false;
    return Math.abs(cart.total_price - calculateExpectedTotal(cart)) <= TOTAL_TOLERANCE_PAISE;
  }

  function isBundleEligible(cart) {
    return getBundleSavings(cart) > 0;
  }

  // ─── Discount code helpers ─────────────────────────────────────────────────

  function getVaahiniCodesFromCart(cart) {
    const codes = [
      ...(cart?.discount_codes || []).map((d) => (typeof d === 'string' ? d : d?.code)),
      ...(cart?.cart_level_discount_applications || []).map((d) => d.title),
    ];

    (cart?.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const app = allocation.discount_application;
        if (app?.type === 'discount_code' && app.title) {
          codes.push(app.title);
        }
      });
    });

    return [...new Set(codes.filter(Boolean).filter((c) => c.startsWith('VAAHINI-')))];
  }

  function getAppliedDiscountsFromDom() {
    const val = document.getElementById('vaahini-applied-discounts')?.value || '';
    return val.split(',').map((c) => c.trim()).filter(Boolean);
  }

  function nonVaahiniDiscounts() {
    return getAppliedDiscountsFromDom().filter((c) => !c.startsWith('VAAHINI-'));
  }

  function discountCodeInCart(cart, code) {
    if (!cart || !code) return false;
    const target = code.toUpperCase();
    const fromApi = getVaahiniCodesFromCart(cart).some((c) => c.toUpperCase() === target);
    if (fromApi) return true;

    return (cart.discount_codes || []).some((d) => {
      const c = typeof d === 'string' ? d : d?.code;
      return c && c.toUpperCase() === target;
    });
  }

  function isDiscountAcceptable(cart, code) {
    if (cartTotalIsCorrect(cart)) return true;
    if (!discountCodeInCart(cart, code)) return false;

    const originalSubtotal = (cart.items || []).reduce(
      (sum, item) => sum + item.original_price * item.quantity,
      0
    );
    return isBundleEligible(cart) && cart.total_price < originalSubtotal;
  }

  // ─── Wipe coupon from every browser store ─────────────────────────────────

  const DISCOUNT_KEY_PATTERNS = [
    'shiprocket',
    'fastrr',
    'pickrr',
    'vaahini',
    'discount',
    'coupon',
    'checkoutorder',
    'checkout',
  ];

  /** Shopify sets this cookie when /discount/CODE is visited — main source of stale coupon. */
  const COOKIES_TO_ALWAYS_DELETE = ['discount_code'];

  let cartCleanupInFlight = null;

  function storageKeyIsDiscountRelated(key) {
    const lower = key.toLowerCase();
    return DISCOUNT_KEY_PATTERNS.some((pattern) => lower.includes(pattern));
  }

  function storageValueHasCoupon(value) {
    if (typeof value !== 'string') return false;
    return (
      /VAAHINI-/i.test(value) ||
      /"discount"\s*:/i.test(value) ||
      /checkoutState/i.test(value)
    );
  }

  function wipeBrowserStorage(storage) {
    try {
      const keysToRemove = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (!key) continue;
        const value = storage.getItem(key);
        if (storageKeyIsDiscountRelated(key) || storageValueHasCoupon(value)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => storage.removeItem(key));
    } catch (_) {}
  }

  function deleteCookieEverywhere(name) {
    const host = location.hostname;
    const domains = new Set([host, '.' + host]);
    const parts = host.split('.');
    if (parts.length >= 2) {
      domains.add('.' + parts.slice(-2).join('.'));
    }
    const paths = ['/', '/cart', '/checkout'];
    const variants = ['', '; Secure', '; Secure; SameSite=Lax', '; Secure; SameSite=None'];

    domains.forEach((domain) => {
      paths.forEach((path) => {
        variants.forEach((suffix) => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}${suffix}`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${suffix}`;
        });
      });
    });
  }

  function cookieShouldBeDeleted(name, value) {
    const lower = name.toLowerCase();
    if (COOKIES_TO_ALWAYS_DELETE.includes(lower)) return true;
    if (DISCOUNT_KEY_PATTERNS.some((pattern) => lower.includes(pattern))) return true;
    if (/VAAHINI-/i.test(value || '')) return true;
    return false;
  }

  function wipeDiscountCookies() {
    try {
      const seen = new Set(COOKIES_TO_ALWAYS_DELETE);
      COOKIES_TO_ALWAYS_DELETE.forEach((name) => deleteCookieEverywhere(name));

      document.cookie.split(';').forEach((part) => {
        const eq = part.indexOf('=');
        if (eq < 0) return;
        const name = part.slice(0, eq).trim();
        const value = decodeURIComponent(part.slice(eq + 1).trim());
        if (!name || seen.has(name)) return;
        if (!cookieShouldBeDeleted(name, value)) return;
        seen.add(name);
        deleteCookieEverywhere(name);
      });
    } catch (_) {}
  }

  async function clearShopifyDiscountCookie() {
    await updateCartDiscounts('').catch(() => {});
    try {
      await nativeFetch('/discount/', { credentials: 'same-origin', redirect: 'manual' });
    } catch (_) {}
    wipeDiscountCookies();
  }

  /** Delete coupon from localStorage, sessionStorage, cookies, and window state. */
  function wipeAllDiscountStorage() {
    window.vaahiniPreGeneratedCode = null;
    window.vaahiniPreGeneratedInvoiceUrl = null;
    window.vaahiniCartVerified = false;
    window.__vaahiniActiveCheckoutDiscount = null;

    wipeBrowserStorage(localStorage);
    wipeBrowserStorage(sessionStorage);
    wipeDiscountCookies();

    try {
      localStorage.removeItem(SHIPROCKET_STORAGE_KEY);
      sessionStorage.removeItem(SHIPROCKET_STORAGE_KEY);
    } catch (_) {}
  }

  function setShiprocketDiscount(code, cart) {
    if (!code) {
      wipeAllDiscountStorage();
      return;
    }
    try {
      localStorage.removeItem(SHIPROCKET_STORAGE_KEY);
      sessionStorage.removeItem(SHIPROCKET_STORAGE_KEY);
    } catch (_) {}
    try {
      localStorage.setItem(
        SHIPROCKET_STORAGE_KEY,
        JSON.stringify({
          discount: code,
          cartFingerprint: getCartFingerprint(cart),
        })
      );
    } catch (_) {}
    window.vaahiniPreGeneratedCode = code;
    window.vaahiniCartVerified = true;
    window.__vaahiniActiveCheckoutDiscount = code;
  }

  /**
   * After qty change: wipe browser storage + at most one cart/update if needed.
   * No retry loops — full strip only runs at checkout.
   */
  async function onCartMutationComplete() {
    if (cartCleanupInFlight) return cartCleanupInFlight;
    if (applying || syncingDiscount) return;

    cartCleanupInFlight = (async () => {
      try {
        wipeAllDiscountStorage();
        const cart = await getCart().catch(() => null);
        if (!cart || isBundleEligible(cart)) return;

        const hasVaahini = getVaahiniCodesFromCart(cart).length > 0;
        const wrongTotal = !cartTotalIsCorrect(cart);
        if (!hasVaahini && !wrongTotal) return;

        await updateCartDiscounts('');
        wipeDiscountCookies();
      } finally {
        cartCleanupInFlight = null;
      }
    })();

    return cartCleanupInFlight;
  }

  /** Checkout / page load: thorough cleanup when cart is not bundle eligible. */
  async function discardDiscountIfNotEligible(cart) {
    if (!cart) cart = await getCart();
    if (isBundleEligible(cart)) return cart;

    wipeAllDiscountStorage();

    const hasVaahini = getVaahiniCodesFromCart(cart).length > 0;
    const wrongTotal = !cartTotalIsCorrect(cart);
    if (hasVaahini || wrongTotal) {
      cart = await updateCartDiscounts('');
      wipeDiscountCookies();
    }

    return cart;
  }

  /** Remove stale VAAHINI codes only — one update max, no retry loop. */
  async function removeStaleVaahiniFromCart(cart) {
    if (!cart) cart = await getCart();
    if (!getVaahiniCodesFromCart(cart).length) return cart;

    const allCodes = [
      ...(cart.discount_codes || []).map((d) => (typeof d === 'string' ? d : d?.code)),
      ...(cart.cart_level_discount_applications || []).map((d) => d.title),
    ].filter(Boolean);
    const remaining = [...new Set(allCodes)]
      .filter((c) => !c.startsWith('VAAHINI-'))
      .join(',');

    return updateCartDiscounts(remaining);
  }

  function getCartFingerprint(cart) {
    if (!cart?.items?.length) return 'empty';
    const itemsKey = cart.items
      .map((item) => `${item.variant_id || item.id}:${item.quantity}:${item.original_price}`)
      .sort()
      .join('|');
    return `${itemsKey}|${calculateExpectedTotal(cart)}`;
  }

  // ─── Cart API ──────────────────────────────────────────────────────────────

  async function getCart() {
    const res = await nativeFetch('/cart.js');
    if (!res.ok) throw new Error('cart.js failed');
    return res.json();
  }

  async function updateCartDiscounts(discountValue) {
    syncingDiscount = true;
    try {
      const res = await nativeFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: discountValue }),
      });
      if (!res.ok) throw new Error('cart discount update failed');
      return res.json();
    } finally {
      syncingDiscount = false;
    }
  }

  const shopDomain = () =>
    document.querySelector('meta[name="vaahini-shop-domain"]')?.content ||
    (typeof Shopify !== 'undefined' && Shopify.shop) ||
    location.hostname;

  async function createDiscountCode(cart) {
    if (!cart.items?.length || cart.currency !== 'INR') {
      throw new Error('Cart not eligible for bundle discount');
    }

    const res = await nativeFetch(`${APP_URL}/api/discount`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shop: shopDomain(),
        currency: cart.currency,
        items: cart.items.map((item) => ({
          variantId: `gid://shopify/ProductVariant/${item.variant_id || item.id}`,
          title: item.title,
          price: item.original_price / 100,
          quantity: item.quantity,
          attributes: Object.entries(item.properties || {}).map(([name, value]) => ({ name, value })),
        })),
        customer: window.theme?.customerEmail ? { email: window.theme.customerEmail } : null,
      }),
    });

    const data = await res.json();
    if (!data.success || !data.code) {
      throw new Error('Discount API did not return a code');
    }
    return data;
  }

  async function applyDiscountCode(code) {
    const merged = [...nonVaahiniDiscounts(), code].filter(Boolean).join(',');

    try {
      await nativeFetch(`/discount/${encodeURIComponent(code)}`, {
        credentials: 'same-origin',
        redirect: 'manual',
      });
    } catch (_) {}

    for (let attempt = 0; attempt < 3; attempt++) {
      const cart = await updateCartDiscounts(merged);
      if (isDiscountAcceptable(cart, code)) {
        return cart;
      }
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 120));
      }
    }

    throw new Error('Discount code could not be applied to cart');
  }

  /**
   * The only checkout preparation pipeline.
   * 1. Wait for cart mutations
   * 2. Strip ALL stale Vaahini codes
   * 3. If bundle eligible → create fresh code → apply → verify total
   * 4. If not eligible → verify cart has no stale discount
   */
  async function prepareCartForCheckout() {
    await waitForCartMutations();

    let cart = await getCart();
    if (!isBundleEligible(cart)) {
      cart = await discardDiscountIfNotEligible(cart);
      if (!cartTotalIsCorrect(cart)) {
        throw new Error('Cart total is incorrect after removing bundle discount');
      }
      return { cart, discountCode: null };
    }

    cart = await removeStaleVaahiniFromCart(cart);

    const { code, invoiceUrl } = await createDiscountCode(cart);
    cart = await applyDiscountCode(code);

    if (!isDiscountAcceptable(cart, code)) {
      throw new Error('Bundle discount verification failed');
    }

    window.vaahiniPreGeneratedInvoiceUrl = invoiceUrl || null;
    setShiprocketDiscount(code, cart);
    return { cart, discountCode: code };
  }

  async function waitForCartMutations() {
    if (!cartMutationPromises.length) return;
    await Promise.all(cartMutationPromises.map((p) => p.catch(() => {})));
  }

  function trackCartMutation(promise) {
    cartMutationPromises.push(promise);
    promise.finally(() => {
      cartMutationPromises = cartMutationPromises.filter((p) => p !== promise);
    });
    return promise;
  }

  function onCartChanged() {
    wipeAllDiscountStorage();
  }

  // ─── UI / checkout buttons ─────────────────────────────────────────────────

  function isCheckoutOrBuyButton(el) {
    if (!el) return false;
    const id = (el.id || '').toLowerCase();
    const className = (el.className || '').toLowerCase();
    const name = (el.name || '').toLowerCase();
    return (
      id.includes('checkout') || id.includes('shiprocket') || id.includes('fastrr') || id.includes('sr-') ||
      className.includes('checkout') || className.includes('shiprocket') || className.includes('fastrr') || className.includes('sr-') ||
      name.includes('checkout') || name.includes('shiprocket') || name.includes('fastrr') ||
      el.classList.contains('sr-checkout-visible')
    );
  }

  const checkoutBtn = () => {
    const std = document.getElementById('checkout') || document.querySelector('.cart__checkout-button');
    if (std) return std;
    const buttons = document.querySelectorAll(
      'button, input[type="submit"], input[type="button"], a.button, div[class*="sr-"], div[class*="shiprocket"], div[class*="fastrr"], [role="button"]'
    );
    for (const btn of buttons) {
      if (isCheckoutOrBuyButton(btn)) return btn;
    }
    return null;
  };

  window.vaahiniGetCheckoutBtn = checkoutBtn;

  const isProductPage = () => window.location.pathname.includes('/products/');

  function cartHasItems() {
    // On cart page/drawer, the DOM elements exist
    if (document.querySelectorAll('cart-items-component .cart-items__table-row').length > 0) return true;
    // On product pages, always treat as "has items" so Shiprocket buttons stay enabled.
    // Real cart validation happens inside prepareCartForCheckout() via getCart().
    if (isProductPage()) return true;
    // Fallback: check Shopify global cart count if available
    if (typeof Shopify !== 'undefined' && Shopify.cartCount > 0) return true;
    return false;
  }

  function setAllCheckoutButtonsEnabled(enabled) {
    // On product pages, never disable checkout/Shiprocket buttons based on cart state.
    // Variant availability is already handled by the theme's native disabled attribute.
    if (!enabled && isProductPage()) return;

    document.querySelectorAll(
      'button, input[type="submit"], input[type="button"], a.button, div[class*="sr-"], div[class*="shiprocket"], div[class*="fastrr"], [role="button"]'
    ).forEach((btn) => {
      if (!isCheckoutOrBuyButton(btn)) return;
      btn.disabled = !enabled;
      btn.style.pointerEvents = enabled ? '' : 'none';
      btn.style.opacity = enabled ? '' : '0.6';
    });
  }

  function setLoading(on, message, clickedButton) {
    window.vaahiniCheckoutButtonLoading = on;

    if (window.VaahiniLoader) {
      if (on) window.VaahiniLoader.show({ immediate: true, pin: true });
      else window.VaahiniLoader.release();
    }

    document.querySelectorAll(
      'button, input[type="submit"], input[type="button"], a.button, div[class*="sr-"], div[class*="shiprocket"], div[class*="fastrr"], [role="button"]'
    ).forEach((button) => {
      if (!isCheckoutOrBuyButton(button)) return;

      if (on) {
        button.style.pointerEvents = 'none';
        button.style.opacity = '0.6';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        const isStandard =
          button.id === 'checkout' ||
          button.name === 'checkout' ||
          button.classList.contains('cart__checkout-button');
        if (isStandard && button === clickedButton) {
          if (!button.dataset.originalText) button.dataset.originalText = button.innerHTML;
          button.innerHTML = `<span class="loading-spinner">${message || 'Preparing checkout...'}</span>`;
        }
      } else {
        button.style.pointerEvents = '';
        button.style.opacity = '';
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (button.dataset.originalText) {
          button.innerHTML = button.dataset.originalText;
          delete button.dataset.originalText;
        }
      }
    });

    if (!on) setAllCheckoutButtonsEnabled(cartHasItems());
  }

  window.vaahiniResetState = () => {
    wipeAllDiscountStorage();
    setLoading(false);
  };

  function patchShiprocketHandler() {
    const original = window.shiprocketCheckoutBuyCartHandler;
    if (typeof original !== 'function' || original.__vaahiniPatched) return;

    window.shiprocketCheckoutBuyCartHandler = function patchedShiprocketCheckout(...args) {
      if (!window.__vaahiniActiveCheckoutDiscount) {
        wipeAllDiscountStorage();
      } else {
        setShiprocketDiscount(window.__vaahiniActiveCheckoutDiscount, null);
      }
      return original.apply(this, args);
    };
    window.shiprocketCheckoutBuyCartHandler.__vaahiniPatched = true;
  }

  function ensureShiprocketLoaded() {
    window.__vaahiniLoadShiprocket?.();
    if (typeof window.shiprocketCheckoutBuyCartHandler === 'function') {
      patchShiprocketHandler();
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        if (typeof window.shiprocketCheckoutBuyCartHandler === 'function') {
          clearInterval(timer);
          patchShiprocketHandler();
          resolve();
        } else if (attempts >= 40) {
          clearInterval(timer);
          // SDK didn't register handler in 4s — force re-trigger script injection
          console.warn('[Vaahini] Shiprocket handler not ready after 4s, retrying...');
          window.__vaahiniLoadShiprocket?.();
          // Second window: 2s more after re-injection
          let retryAttempts = 0;
          const retryTimer = setInterval(() => {
            retryAttempts += 1;
            if (typeof window.shiprocketCheckoutBuyCartHandler === 'function') {
              clearInterval(retryTimer);
              patchShiprocketHandler();
              resolve();
            } else if (retryAttempts >= 20) {
              clearInterval(retryTimer);
              // Still loaded — just resolve so we can attempt invocation.
              // invokeShiprocketCheckout has its own fallback.
              console.warn('[Vaahini] Shiprocket handler not found after 6s total, proceeding with fallback');
              resolve();
            }
          }, 100);
        }
      }, 100);
    });
  }

  function finalCheckoutGuard(cart, discountCode) {
    if (isBundleEligible(cart)) {
      if (!discountCode || !isDiscountAcceptable(cart, discountCode)) {
        throw new Error('Bundle discount missing before checkout');
      }
      setShiprocketDiscount(discountCode, cart);
      return cart;
    }

    if (!cartTotalIsCorrect(cart)) {
      throw new Error('Cart total mismatch before checkout');
    }
    if (getVaahiniCodesFromCart(cart).length) {
      throw new Error('Stale bundle discount still on cart');
    }
    wipeAllDiscountStorage();
    return cart;
  }

  function invokeShiprocketCheckout(button, discountCode) {
    const target = button || checkoutBtn();
    if (!target) return false;

    target.disabled = false;
    target.style.pointerEvents = '';
    target.style.opacity = '';
    target.removeAttribute('aria-busy');

    window.__vaahiniActiveCheckoutDiscount = discountCode || null;
    if (discountCode) {
      setShiprocketDiscount(discountCode, null);
    } else {
      wipeAllDiscountStorage();
    }

    patchShiprocketHandler();

    if (typeof window.shiprocketCheckoutBuyCartHandler === 'function') {
      window.shiprocketCheckoutBuyCartHandler.call(target);
      return true;
    }

    if (typeof target.onclick === 'function') {
      target.onclick.call(target, new MouseEvent('click', { bubbles: true, cancelable: true }));
    } else {
      target.click();
    }
    return true;
  }

  async function releaseCheckout(button, discountCode) {
    clickPassThrough = true;
    submitPassThrough = true;

    await ensureShiprocketLoaded();
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    setLoading(false);

    if (!invokeShiprocketCheckout(button, discountCode)) {
      window.location.href = '/cart';
    }

    // Keep passthrough active while ShipRocket overlay is visible
    // Safety cap: 5 minutes max to prevent infinite polling
    const passThroughStart = Date.now();
    const MAX_PASSTHROUGH_MS = 5 * 60 * 1000;

    const clearPassThrough = () => {
      const overlay = document.getElementById('fastrr-main-container');
      const elapsed = Date.now() - passThroughStart;

      // Keep passthrough while overlay is visible (has layout) and under time cap
      if (overlay && overlay.offsetParent !== null && elapsed < MAX_PASSTHROUGH_MS) {
        setTimeout(clearPassThrough, 500);
        return;
      }

      clickPassThrough = false;
      submitPassThrough = false;
    };
    setTimeout(clearPassThrough, 400);
  }

  async function handleCheckout(event, button) {
    if (clickPassThrough || submitPassThrough) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (applying || window.vaahiniCheckoutButtonLoading) return;
    applying = true;
    setLoading(true, 'Preparing checkout...', button);

    try {
      const { cart, discountCode } = await prepareCartForCheckout();
      finalCheckoutGuard(cart, discountCode);
      await ensureShiprocketLoaded();
      await releaseCheckout(button, discountCode);
    } catch (err) {
      console.error('[Vaahini Checkout]', err);
      wipeAllDiscountStorage();
      await discardDiscountIfNotEligible().catch(() => {});
      setLoading(false);
      // On PDP, ensure buttons stay enabled after error so user can retry
      if (isProductPage()) {
        setAllCheckoutButtonsEnabled(true);
      }
    } finally {
      applying = false;
    }
  }

  // ─── Cart mutation hooks: invalidate only, strip after change ──────────────

  function isUserCartMutation(mutationUrl) {
    return (
      mutationUrl.includes('/cart/add') ||
      mutationUrl.includes('/cart/change') ||
      mutationUrl.includes('/cart/clear') ||
      (mutationUrl.includes('/cart/update') && !syncingDiscount)
    );
  }

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : '');

    // Fast path: skip interception for non-cart URLs
    if (!url.includes('/cart/')) {
      return nativeFetch(input, init);
    }

    if (
      isUserCartMutation(url) &&
      !applying &&
      !syncingDiscount
    ) {
      onCartChanged();
      return trackCartMutation(
        nativeFetch(input, init).then(async (response) => {
          if (response.ok) await onCartMutationComplete();
          return response;
        })
      );
    }

    return nativeFetch(input, init);
  };

  const originalXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...args) {
    this._isCartMutation =
      typeof url === 'string' &&
      (url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear'));
    return originalXHROpen.apply(this, [method, url, ...args]);
  };

  const originalXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (body) {
    if (this._isCartMutation && !applying && !syncingDiscount) {
      onCartChanged();
      this.addEventListener(
        'load',
        () => {
          if (!applying) onCartMutationComplete();
        },
        { once: true }
      );
    }
    return originalXHRSend.apply(this, [body]);
  };

  document.addEventListener('cart:update', () => {
    onCartChanged();
    setAllCheckoutButtonsEnabled(cartHasItems());
  });

  document.addEventListener(
    'click',
    (event) => {
      if (clickPassThrough) return;
      if (!(event.target instanceof Element)) return;

      // Never intercept clicks inside ShipRocket's own checkout overlay
      if (event.target.closest('#fastrr-main-container')) return;

      const button = event.target.closest(
        'button, input[type="submit"], input[type="button"], a.button, div[class*="sr-"], div[class*="shiprocket"], div[class*="fastrr"], [role="button"]'
      );
      if (!button || button.disabled) return;
      if (!isCheckoutOrBuyButton(button)) return;
      if (button.closest('.buy-buttons-block, .product-form-buttons, form[action*="/cart/add"], product-form-component, .product-info')) return;
      if (applying || window.vaahiniCheckoutButtonLoading) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      handleCheckout(event, button);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (submitPassThrough) return;
      if (event.target?.id !== 'cart-form') return;
      const submitter = event.submitter;
      if (!submitter || !isCheckoutOrBuyButton(submitter)) return;
      handleCheckout(event, submitter);
    },
    true
  );

  window.vaahiniRefreshCartOnOpen = () => {
    onCartChanged();
  };

  async function initCartSession() {
    try {
      await discardDiscountIfNotEligible();
    } catch (_) {
    } finally {
      setAllCheckoutButtonsEnabled(cartHasItems());
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartSession);
  } else {
    initCartSession();
  }

  window.addEventListener('pageshow', () => {
    if (!applying) initCartSession();
  });

  window.vaahiniPreGenerate = prepareCartForCheckout;
})();